import {Box, Checkbox, FormControlLabel, Typography} from "@mui/material";
import {
    CAPABILITY_DESCRIPTIONS,
    CAPABILITY_LABELS,
    GRANTABLE_CAPABILITIES,
} from "../../enums/Capabilities";
import type {Capability} from "../../enums/Capabilities";
import {colors} from "../ui/colors";

export type ManagerCapabilitiesPickerProps = {
    value: Capability[];
    onChange: (next: Capability[]) => void;
};

/**
 * Which company-admin abilities this manager receives.
 *
 * <p>Only the ones that are actually a choice. Everything a TRAINER can do, a manager holds already
 * — they sit above a trainer — so those are not listed here at all: neither as checkboxes (one that
 * cannot be unticked reads as a decision the admin is being denied) nor as a read-only summary of
 * abilities nobody asked about. {@link GRANTABLE_CAPABILITIES} is derived from the baseline, so this
 * list shrinks by itself the day a permission becomes inherited.
 *
 * <p>Every box starts unticked because the server is default-deny, and that asymmetry is deliberate:
 * if a manager were "admin minus an exclusion list", every future admin feature would be handed to
 * every existing manager the day it shipped.
 *
 * <p>Hiding UI is presentation only; the server enforces each permission independently.
 */
const ManagerCapabilitiesPicker = ({value, onChange}: ManagerCapabilitiesPickerProps) => {

    const toggle = (capability: Capability) => {
        onChange(value.includes(capability)
            ? value.filter(c => c !== capability)
            : [...value, capability]);
    };

    return (
        <Box>
            <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, marginBottom: "6px"}}>
                הרשאות
            </Typography>
            <Typography sx={{fontSize: "12px", color: colors.textSecondary, marginBottom: "10px"}}>
                בחר אילו יכולות של מנהל חברה למסור למנהל הזה. ללא סימון — אין לו הרשאה כלל.
            </Typography>

            {GRANTABLE_CAPABILITIES.map(capability => (
                <Box key={capability} sx={{marginBottom: "8px"}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={value.includes(capability)}
                                onChange={() => toggle(capability)}
                                inputProps={{"aria-label": CAPABILITY_LABELS[capability]}}
                            />
                        }
                        label={
                            <Typography sx={{fontSize: "14px"}}>{CAPABILITY_LABELS[capability]}</Typography>
                        }
                    />
                    <Typography sx={{fontSize: "12px", color: colors.textSecondary, marginRight: "32px"}}>
                        {CAPABILITY_DESCRIPTIONS[capability]}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

export default ManagerCapabilitiesPicker;
